/**
 * Designed emails (ADR email-design-1): a block document (logo, heading, paragraph, image,
 * button, divider, spacer, two columns, callout, social links, contact footer) with a theme, or a
 * raw HTML document (sanitized on the server before it is stored or sent).
 *
 * This file holds the zod model and small helpers shared by the API (validation, sending), the
 * web editor (live preview) and the AI fake handler. Rendering lives in `emailRender.ts`.
 */
import { z } from 'zod';

export const EMAIL_DESIGN_VERSION = 1 as const;
/** Gmail clips messages above ~102 KB: the rendered/sanitized HTML must stay below this. */
export const MAX_EMAIL_HTML = 100_000;
/** Raw HTML a user may paste before sanitization. */
export const MAX_EMAIL_HTML_INPUT = 250_000;
export const MAX_EMAIL_BLOCKS = 60;
export const MAX_COLUMN_CELLS = 6;

const Hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, { message: 'invalid_color' });
const BlockId = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[A-Za-z0-9_-]+$/);
const Align = z.enum(['left', 'center', 'right']);
/** Text with merge variables and light formatting (**bold**, _italic_, [label](https://…)). */
const RichText = z.string().max(5000);
const ShortText = z.string().max(300);
/** Link target in a block: http(s), mailto: or tel:. Empty = not set yet (send validation flags it). */
const Href = z
  .string()
  .max(2000)
  .refine((u) => u === '' || u === FORM_LINK_HREF || isAllowedHref(u), { message: 'invalid_url' });

/**
 * The one merge token a block link may hold: the personal form link of a campaign linked to a
 * form (forms engine). The renderer substitutes the server-built URL (trusted, never contact data).
 */
export const FORM_LINK_HREF = '{{formLink}}';
/**
 * Image source in a block: https only (uploads are served from the API's public origin). Remote
 * images can reveal opens to their host, as in any email; http would also be mixed content.
 */
const ImageSrc = z
  .string()
  .max(2000)
  .refine((u) => u === '' || /^https:\/\/[^\s<>"'{}]+$/i.test(u), { message: 'invalid_image_url' });

export const EMAIL_FONTS = ['sans', 'serif', 'humanist', 'classic'] as const;
export type EmailFont = (typeof EMAIL_FONTS)[number];

/**
 * Email-safe font stacks with Greek coverage (no web fonts: most clients ignore them). `sans` is
 * the GrowHero face: Commissioner where it is installed, else the platform UI sans (San Francisco,
 * Segoe UI, Roboto), which keeps the same clean, heavy-heading look.
 */
export const EMAIL_FONT_STACKS: Record<EmailFont, string> = {
  sans: "Commissioner,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Helvetica,Arial,sans-serif",
  serif: "Georgia,'Times New Roman',Times,serif",
  humanist: "'Trebuchet MS','Lucida Grande',Tahoma,Verdana,sans-serif",
  classic: "'Palatino Linotype',Palatino,'Book Antiqua',Georgia,serif",
};

/**
 * GrowHero's own email palette (A3 «Δάφνη θαλασσί»): the defaults of a new design and of the
 * system templates. A tenant's brand colour replaces `brand` (see `isDefaultEmailBrand`).
 */
export const EMAIL_PALETTE = {
  /** Primary blue on light surfaces, 7.2:1 on white. */
  brand: '#1F5A8E',
  /** Deep sea-blue brand field (white text 11.7:1, saffron 5.3:1). */
  field: '#0F3A5F',
  /** Cool stone page with a faint blue bias. */
  background: '#F2F5F8',
  /** Pale blue tint (offer page, soft callouts). */
  tint: '#E2ECF5',
  content: '#FFFFFF',
  ink: '#0E1C2B',
  inkMuted: '#3A4A5C',
  line: '#E1E7EE',
  /** Saffron: CTAs, stars, key numbers (dark text on it). */
  saffron: '#E3A23B',
  /** Saffron for small text on the blue field, 7.4:1. */
  saffronLight: '#F2C878',
  saffronTint: '#FBEBCB',
  /** Saffron-family text on light, 7.2:1. */
  saffronInk: '#7A4E0C',
  /** Messages & customers teal (text/buttons on white, 6.9:1). */
  teal: '#136562',
  tealTint: '#E3F3F2',
} as const;

/** Brand colours that mean "GrowHero default" (current + pre-A3 laurel), never a tenant's choice. */
const DEFAULT_BRANDS = new Set([EMAIL_PALETTE.brand, '#1F5D45']);
/** True when `hex` is a GrowHero default brand colour (a template the tenant's brand may replace). */
export function isDefaultEmailBrand(hex: string | null | undefined): boolean {
  return !!hex && DEFAULT_BRANDS.has(hex.toUpperCase() as typeof EMAIL_PALETTE.brand);
}

export const EmailThemeSchema = z.object({
  /** Buttons, links, accents (tenant brand colour by default). */
  brandColor: Hex.default(EMAIL_PALETTE.brand),
  /** Page around the card. */
  backgroundColor: Hex.default(EMAIL_PALETTE.background),
  /** The card itself. */
  contentColor: Hex.default(EMAIL_PALETTE.content),
  textColor: Hex.default(EMAIL_PALETTE.ink),
  font: z.enum(EMAIL_FONTS).default('sans'),
  headingFont: z.enum(EMAIL_FONTS).default('sans'),
  /** Card width in px (600 is the email standard). */
  width: z.number().int().min(480).max(680).default(600),
  radius: z.number().int().min(0).max(24).default(10),
});
export type EmailTheme = z.infer<typeof EmailThemeSchema>;

const LogoBlock = z.object({
  id: BlockId,
  type: z.literal('logo'),
  /** null = the business logo from Settings (or the business name as a wordmark). */
  src: ImageSrc.nullable().default(null),
  alt: ShortText.default(''),
  width: z.number().int().min(40).max(280).default(120),
  align: Align.default('center'),
});
const HeadingBlock = z.object({
  id: BlockId,
  type: z.literal('heading'),
  text: ShortText,
  level: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(1),
  align: Align.default('left'),
  color: Hex.nullable().default(null),
});
const ParagraphBlock = z.object({
  id: BlockId,
  type: z.literal('paragraph'),
  text: RichText,
  align: Align.default('left'),
  size: z.enum(['sm', 'md', 'lg']).default('md'),
  color: Hex.nullable().default(null),
});
const ImageBlock = z.object({
  id: BlockId,
  type: z.literal('image'),
  src: ImageSrc,
  alt: ShortText.default(''),
  /** Percentage of the content width. */
  width: z.number().int().min(10).max(100).default(100),
  href: Href.default(''),
  align: Align.default('center'),
  rounded: z.boolean().default(true),
});
const ButtonBlock = z.object({
  id: BlockId,
  type: z.literal('button'),
  label: z.string().max(80),
  url: Href,
  align: Align.default('center'),
  variant: z.enum(['solid', 'outline']).default('solid'),
  color: Hex.nullable().default(null),
  fullWidth: z.boolean().default(false),
});
const DividerBlock = z.object({
  id: BlockId,
  type: z.literal('divider'),
  color: Hex.nullable().default(null),
  thickness: z.number().int().min(1).max(4).default(1),
});
const SpacerBlock = z.object({
  id: BlockId,
  type: z.literal('spacer'),
  height: z.number().int().min(4).max(96).default(24),
});
const CalloutBlock = z.object({
  id: BlockId,
  type: z.literal('callout'),
  title: ShortText.default(''),
  text: RichText.default(''),
  tone: z.enum(['brand', 'accent', 'soft']).default('soft'),
  align: Align.default('center'),
});
export const SOCIAL_NETWORKS = [
  'facebook',
  'instagram',
  'tiktok',
  'youtube',
  'x',
  'linkedin',
  'google',
  'website',
] as const;
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];
const SocialBlock = z.object({
  id: BlockId,
  type: z.literal('social'),
  items: z
    .array(z.object({ network: z.enum(SOCIAL_NETWORKS), url: Href }))
    .max(8)
    .default([]),
  align: Align.default('center'),
});
const FooterBlock = z.object({
  id: BlockId,
  type: z.literal('footer'),
  /** Address, phone, opening hours… (the unsubscribe line is always added by the server). */
  text: RichText.default(''),
  align: Align.default('center'),
});

/** What a column may hold (no nesting). */
export const EmailCellSchema = z.discriminatedUnion('type', [
  HeadingBlock,
  ParagraphBlock,
  ImageBlock,
  ButtonBlock,
]);
export type EmailCell = z.infer<typeof EmailCellSchema>;

const ColumnsBlock = z.object({
  id: BlockId,
  type: z.literal('columns'),
  ratio: z.enum(['50-50', '40-60', '60-40']).default('50-50'),
  left: z.array(EmailCellSchema).max(MAX_COLUMN_CELLS).default([]),
  right: z.array(EmailCellSchema).max(MAX_COLUMN_CELLS).default([]),
});

export const EmailBlockSchema = z.discriminatedUnion('type', [
  LogoBlock,
  HeadingBlock,
  ParagraphBlock,
  ImageBlock,
  ButtonBlock,
  DividerBlock,
  SpacerBlock,
  ColumnsBlock,
  CalloutBlock,
  SocialBlock,
  FooterBlock,
]);
export type EmailBlock = z.infer<typeof EmailBlockSchema>;
export type EmailBlockInput = z.input<typeof EmailBlockSchema>;
export type EmailBlockType = EmailBlock['type'];
export const EMAIL_BLOCK_TYPES = [
  'logo',
  'heading',
  'paragraph',
  'image',
  'button',
  'divider',
  'spacer',
  'columns',
  'callout',
  'social',
  'footer',
] as const satisfies readonly EmailBlockType[];

export const EmailBlocksDesignSchema = z.object({
  version: z.literal(EMAIL_DESIGN_VERSION).default(EMAIL_DESIGN_VERSION),
  mode: z.literal('blocks'),
  /** Inbox preview text shown after the subject. */
  preheader: z.string().max(200).default(''),
  theme: EmailThemeSchema.prefault({}),
  blocks: z.array(EmailBlockSchema).max(MAX_EMAIL_BLOCKS),
});
export const EmailHtmlDesignSchema = z.object({
  version: z.literal(EMAIL_DESIGN_VERSION).default(EMAIL_DESIGN_VERSION),
  mode: z.literal('html'),
  preheader: z.string().max(200).default(''),
  /** The user's own HTML. Stored and sent only after server-side sanitization. */
  html: z.string().max(MAX_EMAIL_HTML_INPUT),
});
export const EmailDesignSchema = z.discriminatedUnion('mode', [
  EmailBlocksDesignSchema,
  EmailHtmlDesignSchema,
]);
export type EmailDesign = z.infer<typeof EmailDesignSchema>;
export type EmailDesignInput = z.input<typeof EmailDesignSchema>;
export type EmailBlocksDesign = z.infer<typeof EmailBlocksDesignSchema>;
export type EmailHtmlDesign = z.infer<typeof EmailHtmlDesignSchema>;

// ─── URL rules ───────────────────────────────────────────────────────────────

/** http(s), mailto: and tel: only (no javascript:, data:, relative or protocol-relative links). */
export function isAllowedHref(raw: string): boolean {
  // Browsers ignore embedded tabs/newlines/control chars in schemes ("java\tscript:").
  const u = raw.replace(/[\u0000- \u007f-\u009f]/g, '');
  if (/^https?:\/\/[^\s<>"']+$/i.test(u)) return true;
  if (/^mailto:[^\s<>"']+$/i.test(u)) return true;
  if (/^tel:\+?[0-9()\-. ]{3,30}$/i.test(u)) return true;
  return false;
}

// ─── helpers (editor, AI, templates) ─────────────────────────────────────────

let seq = 0;
/** Short unique block id (editor + fake AI). */
export function newBlockId(prefix = 'b'): string {
  seq = (seq + 1) % 1_000_000;
  return `${prefix}${Date.now().toString(36).slice(-4)}${seq.toString(36)}${Math.random()
    .toString(36)
    .slice(2, 5)}`;
}

export function defaultEmailTheme(brandColor?: string | null): EmailTheme {
  return EmailThemeSchema.parse(
    brandColor && /^#[0-9a-fA-F]{6}$/.test(brandColor) ? { brandColor } : {},
  );
}

/** A block of `type` with sensible defaults (labels are filled in by the caller). */
export function createBlock(
  type: EmailBlockType,
  patch: Record<string, unknown> = {},
  id = newBlockId(),
): EmailBlock {
  const base: Record<EmailBlockType, Record<string, unknown>> = {
    logo: {},
    heading: { text: '' },
    paragraph: { text: '' },
    image: { src: '', alt: '' },
    button: { label: '', url: '' },
    divider: {},
    spacer: {},
    columns: {},
    callout: {},
    social: {},
    footer: {},
  };
  return EmailBlockSchema.parse({ ...base[type], ...patch, id, type });
}

/** Parse a stored design (older rows get defaults); null when it is not a valid design. */
export function parseEmailDesign(raw: unknown): EmailDesign | null {
  const r = EmailDesignSchema.safeParse(raw);
  return r.success ? r.data : null;
}

/** Every block, column cells included, in document order. */
export function flattenBlocks(design: EmailBlocksDesign): (EmailBlock | EmailCell)[] {
  const out: (EmailBlock | EmailCell)[] = [];
  for (const b of design.blocks) {
    out.push(b);
    if (b.type === 'columns') out.push(...b.left, ...b.right);
  }
  return out;
}

/** Every author-written text of a design (merge-variable checks). */
export function designTexts(design: EmailDesign): string[] {
  if (design.mode === 'html') return [design.preheader, design.html];
  const out = [design.preheader];
  for (const b of flattenBlocks(design)) {
    if (b.type === 'heading' || b.type === 'paragraph' || b.type === 'footer') out.push(b.text);
    else if (b.type === 'button') out.push(b.label);
    else if (b.type === 'callout') out.push(b.title, b.text);
    else if (b.type === 'image' || b.type === 'logo') out.push(b.alt);
  }
  return out.filter(Boolean);
}

const MD_LINK_RE = /\[([^\]]{1,200})\]\(([^)\s]+)\)/g;

/** Link targets of a block design (buttons, linked images, text links, social). */
export function designLinks(design: EmailBlocksDesign): string[] {
  const out: string[] = [];
  const add = (u: string) => {
    if (u && !out.includes(u)) out.push(u);
  };
  for (const b of flattenBlocks(design)) {
    if (b.type === 'button') add(b.url);
    else if (b.type === 'image') add(b.href);
    else if (b.type === 'social') b.items.forEach((i) => add(i.url));
    if (b.type === 'paragraph' || b.type === 'footer' || b.type === 'callout')
      for (const m of b.text.matchAll(MD_LINK_RE)) add(m[2]!);
  }
  return out;
}

export interface EmailDesignIssue {
  code:
    | 'button_missing_url'
    | 'button_missing_label'
    | 'image_missing_src'
    | 'image_missing_alt'
    | 'empty_design'
    | 'invalid_url';
  blockId?: string;
  /** Errors block sending; warnings are advisory. */
  severity: 'error' | 'warning';
}

/** Does the design link to the personal form page anywhere (button, image, text link, HTML)? */
export function designUsesFormLink(design: EmailDesign): boolean {
  if (design.mode === 'html') return /href\s*=\s*"\s*\{\{\s*formLink\s*\}\}\s*"/i.test(design.html);
  if (designLinks(design).includes(FORM_LINK_HREF)) return true;
  return designTexts(design).some((t) => /\{\{\s*formLink\s*\}\}/.test(t));
}

/** Content problems of a block design (send validation + editor hints). */
export function designIssues(design: EmailDesign): EmailDesignIssue[] {
  if (design.mode === 'html')
    return design.html.trim() ? [] : [{ code: 'empty_design', severity: 'error' }];
  const issues: EmailDesignIssue[] = [];
  const visible = design.blocks.filter((b) => b.type !== 'spacer' && b.type !== 'divider');
  if (visible.length === 0) issues.push({ code: 'empty_design', severity: 'error' });
  for (const b of flattenBlocks(design)) {
    if (b.type === 'button') {
      if (!b.url.trim())
        issues.push({ code: 'button_missing_url', blockId: b.id, severity: 'error' });
      if (!b.label.trim())
        issues.push({ code: 'button_missing_label', blockId: b.id, severity: 'error' });
    }
    if (b.type === 'image') {
      if (!b.src) issues.push({ code: 'image_missing_src', blockId: b.id, severity: 'error' });
      if (!b.alt.trim())
        issues.push({ code: 'image_missing_alt', blockId: b.id, severity: 'warning' });
    }
    if (b.type === 'paragraph' || b.type === 'footer' || b.type === 'callout')
      for (const m of b.text.matchAll(MD_LINK_RE))
        if (m[2] !== FORM_LINK_HREF && !isAllowedHref(m[2]!))
          issues.push({ code: 'invalid_url', blockId: b.id, severity: 'error' });
  }
  return issues;
}

/** Stable JSON (sorted keys) for content hashing of design snapshots. */
export function stableJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  const obj = value as Record<string, unknown>;
  return `{${Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stableJson(obj[k])}`)
    .join(',')}}`;
}

/** Relative luminance (WCAG) of a #rrggbb colour. */
export function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0]! + 0.7152 * ch[1]! + 0.0722 * ch[2]!;
}

/** Ink or white, whichever reads better on `hex`. */
export function readableOn(hex: string): string {
  return luminance(hex) > 0.42 ? EMAIL_PALETTE.ink : '#FFFFFF';
}

/** `hex` mixed towards white by `amount` (0..1) — soft tints for callouts. */
export function tint(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const mix = (v: number) => Math.round(v + (255 - v) * amount);
  const r = mix((n >> 16) & 255);
  const g = mix((n >> 8) & 255);
  const b = mix(n & 255);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase()}`;
}

/** WCAG contrast ratio of two #rrggbb colours (1…21). */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

function mixHex(hex: string, target: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const t = parseInt(target.slice(1), 16);
  const ch = (shift: number) =>
    Math.round(((n >> shift) & 255) + (((t >> shift) & 255) - ((n >> shift) & 255)) * amount);
  return `#${((1 << 24) | (ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).slice(1).toUpperCase()}`;
}

/**
 * `fg` when it reaches `min` contrast on `bg`, else `fg` moved towards black or white (whichever
 * reads on `bg`) just enough to reach it. Keeps a design's intent while guaranteeing legibility
 * (the unsubscribe footer must always be readable).
 */
export function ensureContrast(fg: string, bg: string, min = 4.5): string {
  if (contrastRatio(fg, bg) >= min) return fg;
  const target = luminance(bg) > 0.18 ? '#000000' : '#FFFFFF';
  for (let i = 1; i <= 20; i++) {
    const c = mixHex(fg, target, i / 20);
    if (contrastRatio(c, bg) >= min) return c;
  }
  return target;
}
